import type { ChatMessage } from "@/lib/chat-message-state";

/** Log de atividade exibido na timeline do chat (shape mínimo para cache). */
export type CachedActivityLog = {
  id: string;
  action_type: string;
  created_at: string;
  metadata?: unknown;
  member: { display_name: string | null } | null;
};

export type ChatMessageCacheEntry = {
  messagesByChannel: Record<string, ChatMessage[]>;
  activityLogs: CachedActivityLog[];
  oldestMessageAt: string | null;
  hasMore: boolean;
  fetchedAt: number;
  /** IDs indexados para invalidação por conversa. */
  conversationIds: string[];
};

/**
 * TTL do cache de mensagens por conjunto de conversation_ids.
 *
 * Trade-off 90s (vs 60s): menos refetch em revisitas rápidas; dados podem ficar
 * até 90s defasados se realtime falhar. Realtime INSERT invalida imediatamente.
 */
export const CHAT_MESSAGE_CACHE_TTL_MS = 90_000;

/** Debounce de background refresh — evita thundering herd ao reabrir o mesmo chat. */
const BACKGROUND_REFRESH_DEBOUNCE_MS = 2_000;

const cacheByKey = new Map<string, ChatMessageCacheEntry>();
const inFlightFetches = new Map<string, Promise<unknown>>();
const backgroundRefreshTimers = new Map<string, ReturnType<typeof setTimeout>>();

/** Índice conversation_id → cache keys que contêm esse id (para invalidação pontual). */
const conversationIdToCacheKeys = new Map<string, Set<string>>();

export function makeChatMessageCacheKey(conversationIds: string[]): string {
  return [...conversationIds].sort().join("|");
}

function indexConversationIds(key: string, conversationIds: string[]): void {
  for (const id of conversationIds) {
    let keys = conversationIdToCacheKeys.get(id);
    if (!keys) {
      keys = new Set();
      conversationIdToCacheKeys.set(id, keys);
    }
    keys.add(key);
  }
}

function unindexConversationIds(key: string, conversationIds: string[]): void {
  for (const id of conversationIds) {
    const keys = conversationIdToCacheKeys.get(id);
    if (!keys) continue;
    keys.delete(key);
    if (keys.size === 0) conversationIdToCacheKeys.delete(id);
  }
}

export function isChatMessageCacheStale(
  entry: ChatMessageCacheEntry,
  now = Date.now(),
): boolean {
  return now - entry.fetchedAt >= CHAT_MESSAGE_CACHE_TTL_MS;
}

export function getChatMessageCache(
  conversationIds: string[],
): ChatMessageCacheEntry | null {
  if (conversationIds.length === 0) return null;
  return cacheByKey.get(makeChatMessageCacheKey(conversationIds)) ?? null;
}

export function setChatMessageCache(
  conversationIds: string[],
  data: Omit<ChatMessageCacheEntry, "fetchedAt" | "conversationIds"> & {
    fetchedAt?: number;
  },
): void {
  if (conversationIds.length === 0) return;

  const key = makeChatMessageCacheKey(conversationIds);
  const previous = cacheByKey.get(key);
  if (previous) {
    unindexConversationIds(key, previous.conversationIds);
  }

  const entry: ChatMessageCacheEntry = {
    ...data,
    fetchedAt: data.fetchedAt ?? Date.now(),
    conversationIds: [...conversationIds],
  };
  cacheByKey.set(key, entry);
  indexConversationIds(key, conversationIds);
}

/**
 * Marca entrada como stale sem apagar — usado ao trocar de conversa:
 * revisitas mostram paint instantâneo + refresh em background (se TTL expirou).
 */
export function markChatMessageCacheStale(conversationIds: string[]): void {
  const entry = getChatMessageCache(conversationIds);
  if (!entry) return;
  entry.fetchedAt = 0;
}

export function invalidateChatMessageCache(conversationIds: string[]): void {
  if (conversationIds.length === 0) return;
  const key = makeChatMessageCacheKey(conversationIds);
  const entry = cacheByKey.get(key);
  if (entry) {
    unindexConversationIds(key, entry.conversationIds);
    cacheByKey.delete(key);
  }
  cancelBackgroundRefresh(conversationIds);
}

/** Invalida todas as entradas de cache que incluem o conversation_id. */
export function invalidateChatMessageCacheForConversation(conversationId: string): void {
  const keys = conversationIdToCacheKeys.get(conversationId);
  if (!keys || keys.size === 0) return;

  for (const key of [...keys]) {
    const entry = cacheByKey.get(key);
    if (!entry) continue;
    unindexConversationIds(key, entry.conversationIds);
    cacheByKey.delete(key);
    cancelBackgroundRefresh(entry.conversationIds);
  }
}

/**
 * Deduplica fetches concorrentes para o mesmo conjunto de conversation_ids.
 * Crítico com centenas de usuários reabrindo o mesmo chat simultaneamente.
 */
export function dedupeMessageFetch<T>(
  conversationIds: string[],
  fetcher: () => Promise<T>,
): Promise<T> {
  const key = makeChatMessageCacheKey(conversationIds);
  const existing = inFlightFetches.get(key);
  if (existing) return existing as Promise<T>;

  const promise = fetcher().finally(() => {
    inFlightFetches.delete(key);
  });
  inFlightFetches.set(key, promise);
  return promise;
}

export function cancelBackgroundRefresh(conversationIds: string[]): void {
  const key = makeChatMessageCacheKey(conversationIds);
  const timer = backgroundRefreshTimers.get(key);
  if (timer) {
    clearTimeout(timer);
    backgroundRefreshTimers.delete(key);
  }
}

/**
 * Agenda refresh em background apenas quando stale.
 * Não dispara se document.hidden; debounced por conversation key.
 */
export function scheduleStaleBackgroundRefresh(
  conversationIds: string[],
  refreshFn: () => Promise<void>,
): void {
  const entry = getChatMessageCache(conversationIds);
  if (!entry || !isChatMessageCacheStale(entry)) return;

  const key = makeChatMessageCacheKey(conversationIds);
  const existing = backgroundRefreshTimers.get(key);
  if (existing) clearTimeout(existing);

  const timer = setTimeout(() => {
    backgroundRefreshTimers.delete(key);
    if (typeof document !== "undefined" && document.hidden) return;
    void refreshFn();
  }, BACKGROUND_REFRESH_DEBOUNCE_MS);

  backgroundRefreshTimers.set(key, timer);
}
